import React, { useState, useEffect, useRef } from "react";
import { withRouter } from "react-router-dom";
import axios from "axios";
import { API_URL } from "src/scenes/App";

import AdvertisingTable from "./components/AdvertisingTable";
import DeleteDialog from "./components/DeleteDialog";
import MegaphoneIcon from "src/img/advertising/Megaphone black.svg";
import pluralize from "pluralize";
import { customNames } from "src/utils/helpers";
import {
    BusinessPortalHeader,
    PortalCaption,
    PortalMetricCard,
    PortalMetricGrid,
    PortalMetricLabel,
    PortalMetricValue,
    PortalPageShell,
    PortalSurface,
    Toast,
} from "src/components";
import AdvertisingForm from "./form/AdvertisingForm";
import { AdContext } from "./AdContext";
import { AnimationColors } from "./components/Enums";
import AdvertisingViewsModal from "./components/AdvertisingViewsModal";
import AdvertisingClicksModal from "./components/AdvertisingClicksModal";

export const AUDIENCE = {
    EMPLOYEE: "Employees",
    CLIENT: "Clients",
    EMPLOYEES_CLIENTS: "Employees %26 Clients" // %26 is & character
};

export const FILTER_BY = {
    ACTIVE_ADS: "Active",
    SCHEDULED_ADS: "Scheduled",
    COMPLETED_ADS: "Complete",
};

const Advertising = ({ user }) => {
    const isPollingCancelled = useRef(false);
    const [filterOption, setFilterOption] = useState(FILTER_BY.ACTIVE_ADS);
    const [audienceOption, setAudienceOption] = useState(AUDIENCE.EMPLOYEE);
    const [isFetching, setIsFetching] = useState(false);
    const [advertisementsArray, setAdvertisementsArray] = useState([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isRequestingDeletion, setIsRequestingDeletion] = useState(false);
    const [checkAllAds, setCheckAllAds] = useState(false);
    const [checkedAdvertisements, setCheckedAdvertisements] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [showAdForm, setShowAdForm] = useState(false);
    const [showAdsTable, setShowAdsTable] = useState(true);
    const [adToEdit, setAdToEdit] = useState(null);
    const [companyColorsReady, setCompanyColorsReady] = useState(false);
    const [companyColors, setCompanyColors] = useState({
        primaryColor: AnimationColors.PRIMARY,
        secondaryColor: AnimationColors.SECONDARY,
        backgroundColor: AnimationColors.BACKGROUND,
    });
    const [pageHeadingLabel, setPageHeadingLabel] = useState("Advertising");
    const [advertismentViewershipModalVisibility, setAdvertisementViewersModalVisibility] = useState(false);
    const [advertismentClicksModalVisibility, setAdvertisementClicksModalVisibility] = useState(false);
    const [advertisementData, setAdvertisementData] = useState(null);


    const controllerRef = useRef(new AbortController());

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const fetchVideoByUuid = async (uuid) => {
        const response = await axios.get(`${API_URL}/advertising/video/${uuid}`, {
            headers: { Authorization: `Bearer ${user.token}` },
        });
        return response.data;
    };

    const pollVideoStatus = async (videoUuid) => {
        let synced = false;

        while (!synced && !isPollingCancelled.current) {
            try {
                const res = await fetchVideoByUuid(videoUuid);
                if (res?.pendingMedia === false) {
                    synced = true;
                } else {
                    await delay(5000);
                }
            } catch (err) {
                console.warn("Polling error for video UUID:", videoUuid, err);
                synced = true;
            }
        }
        
        return synced;
    };

    const pollVideosForPendingMedia = async (ads) => {
        if (!ads || ads.length === 0) return;

        isPollingCancelled.current = false;

        const pollingTasks = [];

        for (const ad of ads) {
            const { primaryAsset, verticalAsset } = ad;

            if (primaryAsset?.assetType === "video" && primaryAsset?.pendingMedia) {
                pollingTasks.push(pollVideoStatus(primaryAsset.uuid));
            }

            if (verticalAsset?.assetType === "video" && verticalAsset?.pendingMedia) {
                pollingTasks.push(pollVideoStatus(verticalAsset.uuid));
            }
        }

        if (pollingTasks.length === 0) return;

        await Promise.allSettled(pollingTasks);

        if (!isPollingCancelled.current) {
            await fetchCompanyAds(filterOption, audienceOption);
        }
    };
  
    useEffect(() => {
        const fetchDataAsync = async () => {
            await fetchCompanyAds(FILTER_BY.ACTIVE_ADS, AUDIENCE.EMPLOYEE);
            fetchCompanyColors();
            setIsInitialized(true);
        };
    
        fetchDataAsync();
    }, [user.token]);

    const fetchCompanyAds = async (filterOption, audienceOption) => {
        if (controllerRef.current) {
            isPollingCancelled.current = true; 
            controllerRef.current.abort();
        }

        controllerRef.current = new AbortController();
        let advertisingApi = `${API_URL}/advertising/companyAds`;

        let queryParams = [];

        if (filterOption) {
            queryParams.push(`filter=${filterOption}`);
        }

        if (audienceOption) {
            queryParams.push(`type=${audienceOption}`);
        }

        if (queryParams.length > 0) {
            advertisingApi += `?${queryParams.join("&")}`;
        }

        setIsFetching(true);
        axios.get(advertisingApi, {
            headers: {
                Authorization: "Bearer " + user.token
            },
            signal: controllerRef.current.signal
        }).then((res) => {
            const ads = res.data.companyAds;

            setAdvertisementsArray(ads);
            setIsFetching(false);
           
            const needsPolling = ads.some(ad =>
                (ad.primaryAsset?.assetType === "video" && ad.primaryAsset?.pendingMedia === true) || 
                (ad.verticalAsset?.assetType === "video" && ad.verticalAsset?.pendingMedia === true)
            );

            if (needsPolling) {
                pollVideosForPendingMedia(ads);
            }

        }).catch((error) => {
            if (error !== "canceled") {
                setIsFetching(false);
                console.error("Request failed:", error);
            }
        });
    };

    useEffect(() => {
        return () => {
            isPollingCancelled.current = true;
        };
    }, []);
  
    const fetchCompanyColors = async () => {
        axios.get(`${API_URL}/advertising/companyColors`, {
            headers: {
                Authorization: "Bearer " + user.token
            },
        }).then(res => {
            const { primaryColor, secondaryColor, backgroundColor } = res.data;
            setCompanyColors({
                primaryColor: primaryColor,
                secondaryColor: secondaryColor,
                backgroundColor: backgroundColor,
            });
        }).catch(error => {
            console.warn(error.getApiErrorDetails().message);
        }).finally(res => setCompanyColorsReady(true));
    };

    const handleShowAdForm = () => {
        setShowAdForm(true);
        setShowAdsTable(false);
        setPageHeadingLabel("Create New Ad");
    };

    const handleHideAdForm = () => {
        setShowAdForm(false);
        setShowAdsTable(true);
        setAdToEdit(null);
        setPageHeadingLabel("Advertising");
    };

    useEffect(() => {
        if ((filterOption || audienceOption) && isInitialized) {
            fetchCompanyAds(filterOption, audienceOption);
        }
    }, [filterOption, audienceOption]);

    const handleFilterChange = event => {
        resetListingState();
        setFilterOption(event.target.value);
    };

    const handleAudienceChange = event => {
        resetListingState();
        setAudienceOption(event.target.value);
    };

    const resetListingState = () => {
        setCheckAllAds(false);
        setCheckedAdvertisements([]);
    };

    const handleMultipleDelete = async (advertUuids) => {
        if (controllerRef.current) {
            controllerRef.current.abort();
        }
        controllerRef.current = new AbortController();
        try {
            setIsRequestingDeletion(true);
            await axios.delete(`${API_URL}/advertising/deleteAdvertisements`, {
                data: { advertUuids },
                headers: {
                    Authorization: "Bearer " + user.token
                },
                signal: controllerRef.current.signal
            });

            Toast.success("Selected advertisements have been deleted.");
        } catch {
            Toast.error("Unable to process deletion of advertisement.");
        } finally {
            setIsRequestingDeletion(false);
            fetchCompanyAds(filterOption, audienceOption);
            setCheckedAdvertisements([]);
            handleCloseDeleteDialog();
        }
    };

    const handleCheckAllAds = (ads) => {
        const toggleState = !checkAllAds;
        setCheckAllAds(toggleState);
        const newCheckedUuids = toggleState ? ads.map(ads => ads.uuid) : [];
        setCheckedAdvertisements(newCheckedUuids);
    };

    const handleCheckboxChange = (adUuid) => {
        let newCheckedUuids = [];
        
        if (!checkedAdvertisements.includes(adUuid)) {
            newCheckedUuids = [...checkedAdvertisements, adUuid];
        } else {
            newCheckedUuids = checkedAdvertisements.filter(uuid => uuid !== adUuid);
        }
        if (advertisementsArray.length === newCheckedUuids.length) {
            setCheckAllAds(true);
        } else {
            setCheckAllAds(false);
        }
        setCheckedAdvertisements(newCheckedUuids);
    };

    const handleDelete = () => {
        handleMultipleDelete(checkedAdvertisements);
    };

    const handleShowDeleteDialog = () => {
        if (checkedAdvertisements.length === 0) {
            return Toast.error("Please select an advertisement to delete.");
        }
        if (isFetching) {
            return Toast.error("No data selected yet");
        }
        setShowDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setShowDeleteDialog(false);
    };

    const handleEditAd = (ad) => {
        setAdToEdit(ad);
        setPageHeadingLabel("Edit Ad");
        setShowAdsTable(false);
        setShowAdForm(true);
    };

    const handleShowAdvertisementViewershipModal = (ad) => {
        setAdvertisementData(ad);
        setAdvertisementViewersModalVisibility(true);
    };

    const handleCloseAdvertisementViewershipModal = () => {
        setAdvertisementData(null);
        setAdvertisementViewersModalVisibility(false);
    };

    const handleShowAdvertisementClicksModal = (ad) => {
        setAdvertisementData(ad);
        setAdvertisementClicksModalVisibility(true);
    };

    const handleCloseAdvertisementClicksModal = () => {
        setAdvertisementData(null);
        setAdvertisementClicksModalVisibility(false);
    };

    return (
        <AdContext.Provider value={{
            companyColors: companyColors,
            companyColorsReady: companyColorsReady,
            handleShowAdvertisementViewershipModal: handleShowAdvertisementViewershipModal,
            handleShowAdvertisementClicksModal: handleShowAdvertisementClicksModal
        }}>
            <PortalPageShell
                height={showAdForm ? "calc(100vh - 64px - 1rem)" : "auto"}
                overflow={showAdForm ? "hidden" : "visible"}
                paddingBottom={showAdForm ? "0" : undefined}
            >
                <BusinessPortalHeader
                    title={pageHeadingLabel}
                    description="Create in-app ads with media, headlines, call-to-action links, display frequency, and active campaign timing."
                />
                {!showAdForm && (
                    <PortalSurface padding="1rem">
                        <PortalMetricGrid columns={3}>
                            <PortalMetricCard background="var(--calendar-accent-soft, rgba(91,110,245,0.08))" border="var(--calendar-border-strong, #C9D2E7)">
                                <PortalMetricLabel>Campaigns in view</PortalMetricLabel>
                                <PortalMetricValue>{advertisementsArray.length}</PortalMetricValue>
                            </PortalMetricCard>
                            <PortalMetricCard>
                                <PortalMetricLabel>
                                    Ad Distribution <img src={MegaphoneIcon} height="18px" width="21px" style={{ marginLeft: "0.5rem", verticalAlign: "middle" }} />
                                </PortalMetricLabel>
                                <PortalCaption margin="0.35rem 0 0">
                                    Active ads rotate across Quotes and Business Noticeboard for the selected audience.
                                </PortalCaption>
                            </PortalMetricCard>
                            <PortalMetricCard>
                                <PortalMetricLabel>Audience options</PortalMetricLabel>
                                <PortalCaption margin="0.35rem 0 0">
                                    Target {pluralize(customNames.employees.toLowerCase())}, {pluralize(customNames.clients.toLowerCase())}, or both from the controls below.
                                </PortalCaption>
                            </PortalMetricCard>
                        </PortalMetricGrid>
                    </PortalSurface>
                )}
                {
                    showAdForm &&
                    <AdvertisingForm 
                        adToEdit={adToEdit}
                        fetchCompanyAds={fetchCompanyAds}
                        handleHideAdForm={handleHideAdForm}
                        setFilterOption={setFilterOption}
                        setAudienceOption={setAudienceOption}
                    />
                }

                {
                    showAdsTable &&
                    <AdvertisingTable
                        user={user}
                        advertisementsArray={advertisementsArray}
                        handleShowDeleteDialog={handleShowDeleteDialog}
                        checkedAds={checkedAdvertisements}
                        handleCheckboxChange={handleCheckboxChange}
                        handleCheckAllAds={handleCheckAllAds}
                        checkAllAds={checkAllAds}
                        isFetching={isFetching}
                        filterByOption={filterOption}
                        audienceOption={audienceOption}
                        handleFilterChange={handleFilterChange}
                        handleAudienceChange={handleAudienceChange}
                        handleShowAdForm={handleShowAdForm}
                        handleEditAd={handleEditAd}
                    />
                }
            </PortalPageShell>

            { showDeleteDialog &&
                <DeleteDialog
                    showDeleteDialog={showDeleteDialog}
                    handleCloseDeleteDialog={handleCloseDeleteDialog}
                    handleDelete={handleDelete}
                    isMultipleDelete={checkedAdvertisements.length > 1}
                    isRequestingDeletion={isRequestingDeletion}
                />
            }

            {
                advertismentViewershipModalVisibility &&
                <AdvertisingViewsModal
                    user={user}
                    adData={advertisementData}
                    showAdViewsModal={advertismentViewershipModalVisibility}
                    handleCloseAdViewsModal={handleCloseAdvertisementViewershipModal}
                />
            }

            {
                advertismentClicksModalVisibility &&
                <AdvertisingClicksModal
                    user={user}
                    adData={advertisementData}
                    showAdClicksModal={advertismentClicksModalVisibility}
                    handleCloseAdClicksModal={handleCloseAdvertisementClicksModal}
                />
            }
        </AdContext.Provider>
    );
};

export default withRouter(Advertising);
